import { useDebouncedSearch, useLoading } from '@gorila-shared-ui/components';
import { OnChangeParams } from 'baseui/select';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDebounceFunction } from '../../../hooks/useDebounce';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import { getFleetHealthRuleList } from '../../../services/fleetHealthService';
import { FleetHealthRule } from '../../../types/fleetHealth';
import EnhancedLabeledSelect from '../../ui/EnhancedLabeledSelect';

type Props = {
  fleetHealthRule?: FleetHealthRule;
  selectedRuleId?: string;
  onChangeRuleId?: (fleetHealthRuleId?: string) => void;
  onChangeRule?: (fleetHealthRule?: FleetHealthRule) => void;
  isFilter?: boolean;
  label?: string;
  maxDropdownHeight?: string;
  dropdownOffset?: string;
  inline?: boolean;
  required?: boolean;
  placeholder?: string;
};
export function FleetHealthRuleSelect({
  selectedRuleId,
  onChangeRuleId,
  onChangeRule,
  fleetHealthRule,
  isFilter = true,
  label = 'Regla de evaluación:',
  maxDropdownHeight,
  dropdownOffset,
  inline = false,
  required = false,
  placeholder = 'Selecciona una regla de evaluación',
}: Readonly<Props>) {
  const [fleetHealthRulesList, setFleetHealthRulesList] = useState<FleetHealthRule[]>();
  const { loading, startLoading, stopLoading } = useLoading();
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const { search, setSearch, trigger: debouncedSearch } = useDebouncedSearch('');
  const [selectedFleetHealthRuleOption, setSelectedFleetHealthRuleOption] = useState<{ id: string; label: string }>();
  const requestIdRef = useRef(0);

  useUpdateEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  useEffect(() => {
    if (page === 0) {
      setPage(1);
      return;
    }
    startLoading();
    const loadFleetHealthRules = async () => {
      const requestId = ++requestIdRef.current;
      const { fleetHealthRules, error, hasNext } = await getFleetHealthRuleList(page, search);
      if (requestId === requestIdRef.current) {
        if (!error && fleetHealthRules) {
          if (page > 1 && fleetHealthRulesList) {
            setFleetHealthRulesList([...fleetHealthRulesList, ...fleetHealthRules]);
            setHasNextPage(hasNext);
          } else {
            setFleetHealthRulesList(fleetHealthRules);
            setHasNextPage(hasNext);
          }
        } else {
          setFleetHealthRulesList(undefined);
          setHasNextPage(false);
        }
        stopLoading();
      }
    };
    loadFleetHealthRules();
  }, [page]);

  const fleetHealthRuleOptions = useMemo(() => {
    if (!fleetHealthRulesList) return;
    const fleetHealthRulesOptions: { id: string | undefined; label: string }[] = fleetHealthRulesList.map((fleetHealthRule) => ({
      id: fleetHealthRule._id,
      label: fleetHealthRule.name,
    }));
    if (!required) {
      fleetHealthRulesOptions?.unshift({
        id: undefined,
        label: isFilter ? 'Todas' : 'Ninguna',
      });
    }
    if (fleetHealthRule) {
      if (!fleetHealthRulesOptions.find((c) => c.id === fleetHealthRule._id))
        fleetHealthRulesOptions.push({
          id: fleetHealthRule._id,
          label: fleetHealthRule.name,
        });
    }
    if (selectedFleetHealthRuleOption) {
      if (!fleetHealthRulesOptions.find((c) => c.id === selectedFleetHealthRuleOption.id)) fleetHealthRulesOptions.push(selectedFleetHealthRuleOption);
    }
    return fleetHealthRulesOptions;
  }, [fleetHealthRulesList]);

  const onFleetHealthRuleChange = (params: OnChangeParams) => {
    if (params.option?.id) {
      onChangeRuleId && onChangeRuleId(params.option.id as string);
      onChangeRule &&
        onChangeRule({
          _id: params.option?.id,
          name: params.option?.label,
        } as FleetHealthRule);
    } else {
      onChangeRuleId && onChangeRuleId(undefined);
      onChangeRule && onChangeRule(undefined);
    }
    setSelectedFleetHealthRuleOption(params.option as { id: string; label: string });
    setSearch(undefined);
  };

  const fetchData = () => {
    if (hasNextPage) setPage(page + 1);
  };

  const handleInputChange = useDebounceFunction(function (term: string) {
    setSearch(term);
  }, 500);

  return (
    <EnhancedLabeledSelect
      label={label}
      options={fleetHealthRuleOptions}
      value={selectedRuleId ? [{ id: selectedRuleId }] : undefined}
      onChange={onFleetHealthRuleChange}
      onInputChange={(e) => {
        handleInputChange(e.target.value);
      }}
      infiniteScroll
      fetchData={fetchData}
      fetchDataLoading={loading}
      hasNext={hasNextPage}
      onBlur={() => setSearch(undefined)}
      searchable
      maxDropdownHeight={maxDropdownHeight}
      smallContainer={!!dropdownOffset}
      dropdownOffset={dropdownOffset}
      inline={inline}
      inset={inline}
      fullWidth
      required={required}
      placeholder={placeholder}
    />
  );
}
